import React, { useState } from 'react';

import { Box, Button, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material';

import { isRecord } from '~/utils/isRecord';

import { humanizeKey } from './valueDiff/formatters';

export type ProposedValuePath = Array<string | number>;

interface ProposedValueFieldProps {
  label: string;
  value: unknown;
  previous: unknown;
  path: ProposedValuePath;
  onChange: (path: ProposedValuePath, value: unknown) => void;
}

function createBlankValue(value: unknown, key: string, index: number): unknown {
  if (Array.isArray(value)) {
    if (key === 'configuration') {
      return [{ type: '', quantity: 1 }];
    }
    return [];
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([childKey]) => childKey !== 'id')
        .map(([childKey, childValue]) => [childKey, createBlankValue(childValue, childKey, index)]),
    );
  }

  if (typeof value === 'string') {
    return '';
  }
  if (typeof value === 'number') {
    return key === 'order' || key === 'displayOrder' ? index + 1 : 0;
  }
  if (typeof value === 'boolean') {
    return false;
  }
  return null;
}

function createArrayItem(key: string, sample: unknown, index: number): unknown {
  if (sample !== undefined) {
    return createBlankValue(sample, key, index);
  }
  if (key === 'gallery') {
    return { id_cloudinary_external: '', order: index + 1, filename: null, title: null };
  }
  if (key === 'facilities') {
    return { facilityCode: '', featured: false, displayOrder: index + 1, value: null };
  }
  if (key === 'amenities') {
    return '';
  }
  if (key === 'bed_groups') {
    return { configuration: [{ type: '', quantity: 1 }] };
  }
  if (key === 'commissionable_taxes_and_fees') {
    return { commissionable_type: 'commissionable', content: '' };
  }
  if (key === 'taxes_and_fees') {
    return {};
  }
  return '';
}

export function ProposedValueField({ label, value, previous, path, onChange }: ProposedValueFieldProps) {
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const isDynamicTaxRecord = path.length > 1 && path[path.length - 2] === 'taxes_and_fees';

  if (isRecord(value)) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{label}</Typography>
          {Object.entries(value).map(([key, childValue]) => (
            <ProposedValueField
              key={key}
              label={humanizeKey(key)}
              value={childValue}
              previous={isRecord(previous) ? previous[key] : undefined}
              path={[...path, key]}
              onChange={onChange}
            />
          ))}
          {isDynamicTaxRecord && (
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label="New field"
                value={newFieldName}
                onChange={(event) => setNewFieldName(event.target.value)}
                size="small"
              />
              <TextField
                label="Field value"
                value={newFieldValue}
                onChange={(event) => setNewFieldValue(event.target.value)}
                size="small"
              />
              <Button
                onClick={() => {
                  const key = newFieldName.trim();
                  if (!key || key in value) {
                    return;
                  }
                  onChange(path, { ...value, [key]: newFieldValue });
                  setNewFieldName('');
                  setNewFieldValue('');
                }}
                disabled={!newFieldName.trim() || newFieldName.trim() in value}
              >
                Add field
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  }

  if (Array.isArray(value)) {
    const sample = value[0] ?? (Array.isArray(previous) ? previous[0] : undefined);
    const arrayKey = String(path[path.length - 1]);
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{label}</Typography>
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No items
            </Typography>
          )}
          {value.map((childValue, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ProposedValueField
                  label={`${label} ${index + 1}`}
                  value={childValue}
                  previous={Array.isArray(previous) ? previous[index] : undefined}
                  path={[...path, index]}
                  onChange={onChange}
                />
              </Box>
              <Button
                color="error"
                onClick={() =>
                  onChange(
                    path,
                    value.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
                aria-label={`Remove ${label} ${index + 1}`}
              >
                Remove
              </Button>
            </Stack>
          ))}
          <Button onClick={() => onChange(path, [...value, createArrayItem(arrayKey, sample, value.length)])}>
            Add item
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <FormControlLabel
        label={label}
        control={
          <Switch
            checked={value}
            onChange={(event) => onChange(path, event.target.checked)}
            inputProps={{ 'aria-label': label }}
          />
        }
      />
    );
  }

  const isNumber = typeof value === 'number' || (value === null && typeof previous === 'number');
  const textValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const isLongText = !isNumber && textValue.length >= 80;

  return (
    <TextField
      label={label}
      type={isNumber ? 'number' : 'text'}
      value={textValue}
      onChange={(event) => {
        const input = event.target.value;
        if (isNumber) {
          if (input === '') {
            onChange(path, null);
          } else {
            const numericValue = Number(input);
            if (Number.isFinite(numericValue)) {
              onChange(path, numericValue);
            }
          }
        } else {
          onChange(path, input === '' && value === null ? null : input);
        }
      }}
      multiline={isLongText}
      minRows={isLongText ? 3 : undefined}
      fullWidth
      size="small"
    />
  );
}
